import { DropdownMenu, DropdownTrigger, IconButton, Kebab } from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { Row } from "react-table";

import style from "./file-sharing.scss";
import FileSharingMenuItems from "./FileSharingMenuItems";
import { baseFilename } from "./fileTreeUtils";
import FolderMenuItems from "./FolderMenuItems";
import Chevron from "components/icons/Chevron";
import FileIcon from "components/icons/FileIcon";
import FolderIcon from "components/icons/FolderIcon";
import tableStyle from "components/table/table.scss";
import TextWithTooltip from "components/table/TextWithTooltip";
import { AUTH_FILESHARE_DELETE, AUTH_FILESHARE_EDIT, AUTH_FILESHARE_VIEW } from "domain/authority";
import { FileTreeNode } from "domain/fileSharing";
import { userSessionService } from "services/user/UserSessionService";
import { resolveColor } from "utils/commonFunctions";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const ESTIMATED_MENU_HEIGHT_PIXELS = 200;

const ConnectorLines = ({ depth }: { depth: number }): JSX.Element => (
    <div className={tableStyle.nameCellConnectorLines}>
        {Array.from({ length: depth }, (_, i) => (
            <span key={i} />
        ))}
    </div>
);

const ExpandToggle = ({ isExpanded }: { isExpanded: boolean }): JSX.Element => {
    const theme = useTheme();
    return (
        <Chevron
            color={theme.iconFillColor}
            className={classNames(style.toggleArrow, { [style.right]: !isExpanded, [style.down]: isExpanded })}
            width={20}
            height={20}
        />
    );
};

export interface FilenameCellProps {
    row: Row<FileTreeNode>;
    existingPaths: string[];
    onDrop?: (targetPath: string, e: React.DragEvent<HTMLDivElement>) => void;
    onRefresh: () => void;
    onRefreshQuota: () => void;
}

const FilenameCell = (props: FilenameCellProps) => {
    const handleDeleted = () => {
        props.onRefresh();
        props.onRefreshQuota();
    };

    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const [dropdownPosition, setDropdownPosition] = React.useState<"bottom-start" | "top-start">("bottom-start");
    const cellRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const cellElement = cellRef.current;
        if (!cellElement || !isDropdownOpen) {
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) {
                    setIsDropdownOpen(false);
                }
            },
            { threshold: 0 }
        );
        observer.observe(cellElement);

        let scrollContainer: HTMLElement | null = cellElement.parentElement;
        while (scrollContainer && scrollContainer.scrollWidth <= scrollContainer.clientWidth) {
            scrollContainer = scrollContainer.parentElement;
        }
        const handleScroll = () => setIsDropdownOpen(false);
        scrollContainer?.addEventListener("scroll", handleScroll);

        return () => {
            observer.disconnect();
            scrollContainer?.removeEventListener("scroll", handleScroll);
        };
    }, [isDropdownOpen]);

    const canView = userSessionService.userHasAllAuthorities([AUTH_FILESHARE_VIEW]);
    const canEdit = userSessionService.userHasAllAuthorities([AUTH_FILESHARE_EDIT]);
    const canDelete = userSessionService.userHasAllAuthorities([AUTH_FILESHARE_DELETE]);
    const hasFolderMenuItems = canEdit || canDelete;
    const hasFileMenuItems = canView || canEdit || canDelete;
    const isSubRow = props.row.depth >= 1;
    const node = props.row.original;
    const displayName = node.isFolder ? node.filename : baseFilename(node.filename, node.directoryPath);
    const folderPath = node.isFolder ? node.folderPath : undefined;

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData(
            "application/json",
            JSON.stringify(
                node.isFolder
                    ? {
                          isFolder: true,
                          folderPath: node.folderPath,
                          filename: displayName,
                          directoryPath: node.directoryPath,
                      }
                    : { isFolder: false, uuid: node.uuid, filename: displayName, directoryPath: node.directoryPath }
            )
        );
        e.currentTarget.classList.add(style.dragging);
    };

    const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
        e.currentTarget.classList.remove(style.dragging);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        e.currentTarget.classList.add(style.dragOver);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            e.currentTarget.classList.remove(style.dragOver);
        }
    };

    const hasMenuItems = node.isFolder ? hasFolderMenuItems : hasFileMenuItems;

    const handleKebabClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (cellRef.current) {
            const rect = cellRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setDropdownPosition(spaceBelow < ESTIMATED_MENU_HEIGHT_PIXELS ? "top-start" : "bottom-start");
        }
    };

    const kebabTrigger = hasMenuItems && (
        <div className={style.kebabIcon} onClick={handleKebabClick}>
            <DropdownTrigger>
                <IconButton testId={testIds.common.primaryView.table.kebabMenu.button}>
                    <Kebab color={resolveColor("--theme-icon-fill-default")} />
                </IconButton>
            </DropdownTrigger>
        </div>
    );

    const nameCellDiv = (
        <div
            ref={cellRef}
            className={classNames(style.nameCell, {
                [tableStyle.subRowBorder]: isSubRow || (props.row.depth === 0 && props.row.isExpanded),
                [tableStyle.subRowNameCell]: isSubRow,
                [tableStyle.expandedRow]: props.row.isExpanded,
            })}
            data-depth={props.row.depth}
            draggable={props.onDrop !== undefined}
            onDragStart={props.onDrop !== undefined ? handleDragStart : undefined}
            onDragEnd={props.onDrop !== undefined ? handleDragEnd : undefined}
            onDragOver={folderPath !== undefined && props.onDrop !== undefined ? handleDragOver : undefined}
            onDragLeave={folderPath !== undefined && props.onDrop !== undefined ? handleDragLeave : undefined}
            onDrop={
                folderPath !== undefined && props.onDrop !== undefined ? (e) => props.onDrop!(folderPath, e) : undefined
            }
            data-folder-path={folderPath}
        >
            {isSubRow && <ConnectorLines depth={props.row.depth} />}
            {kebabTrigger}
            {node.isFolder && (
                <button
                    onClick={() => props.row.toggleRowExpanded()}
                    className={style.folderToggleButton}
                    aria-expanded={props.row.isExpanded}
                    data-testid={testIds.common.primaryView.table.expandArrow}
                >
                    <ExpandToggle isExpanded={props.row.isExpanded} />
                </button>
            )}
            <span className={style.itemIcon}>{node.isFolder ? <FolderIcon /> : <FileIcon />}</span>
            <TextWithTooltip text={displayName} />
        </div>
    );

    if (!hasMenuItems) {
        return nameCellDiv;
    }

    // DropdownContent and modal portals render as siblings of nameCellDiv to
    // avoid interfering with its flex layout.
    return (
        <DropdownMenu
            position={dropdownPosition}
            isDropdownOpen={isDropdownOpen}
            onDropdownOpenChange={setIsDropdownOpen}
        >
            {nameCellDiv}
            {node.isFolder ? (
                <FolderMenuItems node={node} onDeleted={handleDeleted} onRenamed={props.onRefresh} />
            ) : (
                <FileSharingMenuItems
                    item={node}
                    existingPaths={props.existingPaths}
                    onDeleted={handleDeleted}
                    onMoved={props.onRefresh}
                    onRenamed={props.onRefresh}
                    onDownloaded={props.onRefreshQuota}
                />
            )}
        </DropdownMenu>
    );
};

export default FilenameCell;
